// frontend/src/hooks/useScrollAwareClick.ts
import { useRef, useCallback } from 'react';

/**
 * Returns an onClick wrapper that suppresses navigation if the user
 * was scrolling (touchmove detected between touchstart and touchend).
 *
 * Touch-only: pointer/mouse input is unaffected (wasScrolling stays false).
 * Tracks both horizontal and vertical movement so it works on lists that
 * scroll horizontally as well (e.g. wide tables).
 *
 * Shared-state note: when used on a component that renders multiple items
 * sharing one hook instance (e.g. Ant Design Table's onRow), the refs are
 * shared across rows. This is safe because a single touch gesture fires
 * touchstart → touchmove → click in order on the same element, and
 * touchstart resets the state at the start of every new gesture.
 *
 * Usage:
 *   const { getClickHandler, touchHandlers } = useScrollAwareClick();
 *   <div {...touchHandlers} onClick={getClickHandler((e) => navigate('/foo'))}>
 */
const SCROLL_THRESHOLD_PX = 10;

export function useScrollAwareClick() {
  const wasScrollingRef = useRef(false);
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    wasScrollingRef.current = false;
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
  }, []);

  const onTouchMove = useCallback((e: React.TouchEvent) => {
    const deltaX = e.touches[0].clientX - touchStartXRef.current;
    const deltaY = e.touches[0].clientY - touchStartYRef.current;
    // If finger moved more than 10px in any direction, it's a scroll
    if (Math.hypot(deltaX, deltaY) > SCROLL_THRESHOLD_PX) {
      wasScrollingRef.current = true;
    }
  }, []);

  const getClickHandler = useCallback(
    <E = unknown>(handler: (e: E) => void) => {
      return (e: E) => {
        if (!wasScrollingRef.current) {
          handler(e);
        }
      };
    },
    []
  );

  const touchHandlers = {
    onTouchStart,
    onTouchMove,
  };

  return { getClickHandler, touchHandlers };
}
